// ============================================================================
// ROLE PERMISSION HOOKS
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { rolePermissionService } from 'api/services/rolePermissionService';
import type {
  RolePermission,
  CreateRolePermissionRequest,
  UpdateRolePermissionRequest,
  RolePermissionFilterParams,
} from 'api/types/rolePermission.types';
import type { PaginatedResponse } from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of role permissions.
 * Refetches automatically when `params` changes.
 */
export const useRolePermissions = (params?: RolePermissionFilterParams) => {
  const [data, setData] = useState<RolePermission[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<RolePermission>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchRolePermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);

  return { data, pagination, loading, error, refetch: fetchRolePermissions };
};

/**
 * Hook to create a new role permission.
 */
export const useCreateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRolePermission = async (
    data: CreateRolePermissionRequest,
    options?: {
      onSuccess?: (item: RolePermission) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createRolePermission, loading, error };
};

/**
 * Hook to update a role permission. Always pass the latest rowVersion.
 */
export const useUpdateRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRolePermission = async (
    id: string,
    data: UpdateRolePermissionRequest,
    options?: {
      onSuccess?: (item: RolePermission) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await rolePermissionService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateRolePermission, loading, error };
};

/**
 * Hook to delete a role permission.
 */
export const useDeleteRolePermission = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRolePermission = async (
    id: string,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    try {
      await rolePermissionService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRolePermission, loading, error };
};
